
import { StatsCard } from "@/components/ui/StatsCard";
import { BanknoteIcon, LineChart, TrendingUpIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfDay, startOfMonth, endOfMonth } from "date-fns";
import { useOwnerCurrency } from "@/hooks/useOwnerCurrency";

export const StatsCards = () => {
  const { data: currency } = useOwnerCurrency();

  const { data: todayStats } = useQuery({
    queryKey: ["today_stats"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const startOfToday = startOfDay(new Date());

      const { data, error } = await supabase
        .from("poultry_transactions")
        .select("total_amount")
        .eq("created_by", user.id)
        .eq("transaction_type", "sale")
        .eq("is_deducted_from_revenue", true)
        .gte("created_at", startOfToday.toISOString());

      if (error) throw error;

      const todayTotal = data?.reduce((sum, record) => sum + (record.total_amount || 0), 0) || 0;
      return todayTotal;
    }
  });

  const { data: monthStats } = useQuery({
    queryKey: ["month_stats"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const monthStart = startOfMonth(new Date());
      const monthEnd = endOfMonth(new Date());

      const { data, error } = await supabase
        .from("poultry_transactions")
        .select("total_amount")
        .eq("created_by", user.id)
        .eq("transaction_type", "sale")
        .eq("is_deducted_from_revenue", true)
        .gte("created_at", monthStart.toISOString())
        .lte("created_at", monthEnd.toISOString());

      if (error) throw error;

      const monthTotal = data?.reduce((sum, record) => sum + (record.total_amount || 0), 0) || 0;
      return monthTotal;
    }
  });

  const { data: yearStats } = useQuery({
    queryKey: ["year_stats"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("poultry_transactions")
        .select("total_amount")
        .eq("created_by", user.id)
        .eq("transaction_type", "sale")
        .eq("is_deducted_from_revenue", true)
        .gte("created_at", new Date(new Date().getFullYear(), 0, 1).toISOString())
        .lte("created_at", new Date(new Date().getFullYear(), 11, 31).toISOString());

      if (error) throw error;

      const yearTotal = data?.reduce((sum, record) => sum + (record.total_amount || 0), 0) || 0;
      return yearTotal;
    }
  });

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <StatsCard
        title="Chiffre d'affaires aujourd'hui"
        value={`${(todayStats || 0).toLocaleString()} ${currency}`}
        icon={BanknoteIcon}
        description={`Mis à jour le ${format(new Date(), 'dd/MM/yyyy')}`}
      />
      <StatsCard
        title="Chiffre d'affaires ce mois"
        value={`${(monthStats || 0).toLocaleString()} ${currency}`}
        icon={TrendingUpIcon}
        description={`Pour le mois de ${format(new Date(), 'MMMM yyyy')}`}
      />
      <StatsCard
        title="Chiffre d'affaires annuel"
        value={`${(yearStats || 0).toLocaleString()} ${currency}`}
        icon={LineChart}
        description={`Pour l'année ${new Date().getFullYear()}`}
      />
    </div>
  );
};
